import CustomScrollbar from 'components/Common/CustomScrollbar';
import TextGenerate from 'components/Common/TextGenerator';
import ClipLineSelector from 'components/Dashboard/ParliamentLive/ClipCreation/ClipLineSelector';
import ClipPreviewPlayer from 'components/Dashboard/ParliamentLive/ClipCreation/ClipPreviewPlayer';
import { computeClipWindow } from 'components/Dashboard/ParliamentLive/ClipCreation/clipWindow';
import CreateClipModal from 'components/Dashboard/ParliamentLive/CreateClipModal';
import { copyTextToClipboard } from 'components/Dashboard/helpers/copyFunctionality';
import createMarkup from 'components/Dashboard/helpers/createMarkup';
import useTitleScrolledStyles from 'components/Dashboard/hooks/useTitleScrolledStyles';
import { ShareParliamentaryLiveContent } from 'components/Dashboard/ParliamentaryRecord/ShareParliamentaryContent';
import ParliamentaryLiveRemarksWidget, {
  getInParameter,
} from 'components/Dashboard/ParliamentLive/ParliamentaryLiveRemarksWidget';
import { ParliamentHeadingElement } from 'components/Dashboard/ParliamentLive/ParliamentLiveItem';
import ParliamentLiveVideoPlayer from 'components/Dashboard/ParliamentLive/ParliamentLiveVideoPlayer';
import TranscriptSearchBar from 'components/Dashboard/ParliamentLive/TranscriptSearchBar';
import useTranscriptSearch from 'components/Dashboard/ParliamentLive/useTranscriptSearch';
import ReferenceTopicWidget from 'components/Dashboard/SubNav/ReferenceTopicsWidget';
import ResearchPopUp from 'components/Dashboard/SubNav/ui/ResearchPopUp';
import preventLink from 'components/Dashboard/TagsList/prevenLink';
import getUrlParam, { decodeUrlQueryValue } from 'components/Dashboard/utilities/getUrlParam';
import { useHeightContainer } from 'components/Dashboard/utilities/useHeightContainer';
import useWindowHeight from 'components/Dashboard/utilities/useWindowHeight';
import { store } from 'components/Store';
import dayjs from 'dayjs';
import React, { useState, useRef, useEffect, useContext, useMemo, useCallback, useLayoutEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';
import time from 'utils/time';
import useOutsideElement from 'components/Dashboard/utilities/useOutsideElement';

const policyMogulLogoUrl = '/policymogul-logo-white.svg';
const SEEK_LEAD_CORRECTION_SECONDS = 5;

const videoSecondsForItem = (item, streamStartTimestamp) => {
  const itemTimestamp = item?.dateTimeTimestamp;
  if (itemTimestamp == null || streamStartTimestamp == null) return null;
  return Math.max(0, itemTimestamp - streamStartTimestamp + SEEK_LEAD_CORRECTION_SECONDS);
};

const TranscriptRow = React.memo(function TranscriptRow({
  item,
  timeParam,
  relevantRemark,
  showTime,
  streamStartTimestamp,
  videoPlayerRef,
  onCreateClip,
  isPreparingClip,
  playingItemId,
  setPlayingItemId,
}) {
  const isPlaying = playingItemId != null && playingItemId === item?.objectID;

  const togglePlay = () => {
    if (isPlaying) {
      videoPlayerRef.current?.pause();
      setPlayingItemId(null);
      return;
    }
    const seconds = videoSecondsForItem(item, streamStartTimestamp);
    if (seconds == null) return;
    videoPlayerRef.current?.seekToTimestamp(seconds);
    setPlayingItemId(item?.objectID ?? null);
  };

  return (
    <div
      className={`reset-link text-main-blue px-4 d-block parliament-live-event break-word ${
        relevantRemark ? 'parliament-live-event-relevant specific-top-padding-28 py-3' : 'py-1'
      } ${showTime ? 'parliament-live-event-with-time' : 'parliament-live-event-without-time'}`}
      id={`event-${timeParam}`}
      onClick={() => {
        const selectedText = window.getSelection().toString().trim();
        if (!selectedText) togglePlay();
      }}
    >
      <ShareParliamentaryLiveContent
        item={item}
        additionalClass={'parliament-live-share-buttons border rounded-pill'}
        togglePlay={togglePlay}
        isPlaying={isPlaying}
      >
        <button
          className='general-button position-relative share-parliamentary-content'
          disabled={isPreparingClip}
          onClick={(e) => {
            preventLink(e);
            onCreateClip(item);
          }}
          data-tooltip-id={'general-tooltip'}
          data-tooltip-content={'Create a clip'}
        >
          <span
            className={
              isPreparingClip
                ? 'fas fa-spinner fa-spin'
                : 'icon-clip-video d-inline-block-centered centered-3 title-h5-m'
            }
          />
        </button>
      </ShareParliamentaryLiveContent>
      {showTime && (
        <p
          className={`${relevantRemark ? '' : 'specific-top-margin-6'} mb-1 flex-centered main-light-text paragraph-p5`}
        >
          <span className='icon-clock mr-1' />
          <span>{time(item?.dateTime)?.formatTime()}</span>
        </p>
      )}
      <p className='paragraph-p1 line-height-1-5 mb-0' dangerouslySetInnerHTML={createMarkup(item.text)} />
    </div>
  );
});

const ParliamentLiveEventContentVideo = (props) => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { activePlan, keywordsLists, referenceState } = state ?? {};
  const { enableAdvancedSearch } = activePlan ?? {};

  const { videoSrc, hits } = props;

  const showWidgetCondition = getUrlParam('topic-id') || getUrlParam('search') || getUrlParam('or');

  const [heightContainer, containerRef] = useHeightContainer();
  const [showPopup, setShowPopup] = useState(false);
  const [keywordsSection, setKeywordsSection] = useState('keywords');
  const [reachedTheEnd, setReachedTheEnd] = useState(false);
  const contentContainer = useRef(null);
  const videoPlayerRef = useRef(null);
  const [clipSelectActive, setClipSelectActive] = useState(false);
  const [playingItemId, setPlayingItemId] = useState(null);

  const handleVideoPlayingChange = useCallback((playing) => {
    if (!playing) setPlayingItemId(null);
  }, []);

  const mainContentProps = {
    ...props,
    contentContainer,
    videoPlayerRef,
    clipSelectActive,
    setClipSelectActive,
    setReachedTheEnd,
    playingItemId,
    setPlayingItemId,
  };

  return (
    <>
      <div className='row px-0 piece-content-container parliament-live-test-content'>
        <div className='general-content-column padding-to-nav p-0 z-100'>
          <div ref={contentContainer}>
            <div
              className='main-content-section content-container'
              ref={containerRef}
              style={{ height: `${heightContainer}px` }}
            >
              <ParliamentLiveEventMainContent {...mainContentProps} />
            </div>
          </div>
        </div>
        <div className='related-content-column show-lg px-0'>
          <div className='related-content'>
            {(enableAdvancedSearch || showWidgetCondition) && (
              <div className='box-outstanding bg-white box-rounded mb-4 parliamentary-widget'>
                {enableAdvancedSearch && (
                  <div className={`p-3 ${showWidgetCondition ? 'border-bottom' : ''} `}>
                    <ReferenceTopicWidget
                      keywordsLists={keywordsLists}
                      setShowPopup={setShowPopup}
                      keywordsSection={keywordsSection}
                      setKeywordsSection={setKeywordsSection}
                      includeMasterTopic={true}
                      fixedPicker={true}
                    />
                  </div>
                )}
                {showWidgetCondition &&
                  (hits?.length > 0 ? (
                    <ParliamentaryLiveRemarksWidget reachedTheEnd={reachedTheEnd} hits={hits} />
                  ) : (
                    <div className='pl-3 py-3'>
                      <p className='mb-0'>No relevant remarks</p>
                    </div>
                  ))}
              </div>
            )}
            {videoSrc && (
              <>
                <ParliamentLiveVideoPlayer
                  ref={videoPlayerRef}
                  src={videoSrc}
                  onPlayingChange={handleVideoPlayingChange}
                />
              </>
            )}
          </div>
        </div>
      </div>

      <ResearchPopUp
        keywordsLists={(keywordsLists ?? []).filter((item) => item.id !== null)}
        setShowPopup={setShowPopup}
        showPopUp={showPopup}
        keywordsSection={keywordsSection}
        referenceState={referenceState}
      />
    </>
  );
};

const ParliamentLiveEventMainContent = (props) => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { realtimeEvents } = state ?? {};

  const {
    eventId,
    eventBodiesState,
    hits,
    navigateRemarks,
    eventItem,
    topicCondition,
    contentContainer,
    videoPlayerRef,
    videoSrc,
    streamStartTimestamp,
    clipSelectActive,
    setClipSelectActive,
    setReachedTheEnd,
    playingItemId,
    setPlayingItemId,
  } = props ?? {};
  const { eventBodies, setEventBodies } = eventBodiesState ?? {};
  const resultInformation = eventBodies?.[0];
  const realTimeEvent = realtimeEvents?.find(
    (item) => item?.parliamentEventId === resultInformation?.parliamentEventId
  );
  const endedEvent = !dayjs().isSame(dayjs(realTimeEvent?.createdAtUtc), 'day') && !getUrlParam('startDateTime');

  const scrollBarRef = useRef(null);
  const titleSection = useRef(null);
  const fixedTitle = useRef(null);
  const goTranscriptionButton = useRef(null);

  const conditionToShowGoToTranscriptionButton = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e ?? scrollBarRef?.current ?? {};
    const valueOfWhiteSpace = 200;
    return scrollTop < scrollHeight - clientHeight - valueOfWhiteSpace;
  };
  const inParameter = getUrlParam('in');
  const [scrollFades, setScrollFades] = useState({ topFade: false, bottomFade: false });
  const [scrolling, setScrolling] = useState(false);
  const [showGoToTranscriptionButton, setShowGoToTranscriptionButton] = useState(
    conditionToShowGoToTranscriptionButton()
  );
  const [transcriptionButtonPosition, setTranscriptionButtonPosition] = useState(null);
  const [animatedItem, setAnimatedItem] = useState(null);
  const [clipSelectItem, setClipSelectItem] = useState(null);
  const updatingContributionRef = useRef(false);
  const transcriptSearch = useTranscriptSearch({ eventId, eventBodiesState, scrollBarRef, updatingContributionRef });

  const [clipPrep, setClipPrep] = useState({
    status: 'idle',
    item: null,
    itemVideoStart: 0,
    itemVideoEnd: 0,
    videoDuration: 0,
    windowStart: 0,
    windowEnd: 0,
    clipStart: 0,
    clipEnd: 0,
    poster: null,
    thumbnails: [],
    prepDone: false,
  });

  const location = useLocation();
  const navigate = useNavigate();
  const { windowWidth, windowHeight } = useWindowHeight();

  const allowScrollBottom = useRef(!inParameter && location.pathname !== '/dashboard');

  const filteredBodies = useMemo(
    () => (eventBodies ?? []).filter((item) => !item.notShown && item.text?.trim()),
    [eventBodies]
  );
  const timeParams = useMemo(
    () => filteredBodies.map((item) => getInParameter({ itemUrl: item?.contentUrl })),
    [filteredBodies]
  );
  const relevantRemarkUrls = useMemo(() => new Set((hits ?? []).map((hit) => hit?.contentUrl)), [hits]);
  const showTimes = useMemo(() => {
    let previous = null;
    return filteredBodies.map((item) => {
      const minute = dayjs(item?.dateTime).format('HH:mm');
      const show = minute !== previous;
      previous = minute;
      return show;
    });
  }, [filteredBodies]);
  const remarkTimeIds = useMemo(() => {
    const map = new Map();
    (hits ?? []).forEach((hit) => {
      const id = getInParameter({ itemUrl: hit?.contentUrl });
      if (id != null) map.set(id, hit);
    });
    return map;
  }, [hits]);

  const updateExtract = useRef(null);
  updateExtract.current = (source) => {
    if (!scrolling && hits?.length > 0 && topicCondition) {
      const remarks = hits.map((item) => {
        return getInParameter({ itemUrl: item?.contentUrl });
      });

      const firstElement = hits[0];
      const elementToUse = firstElement;
      const timeParamElement = getInParameter({ itemUrl: elementToUse?.contentUrl });
      const timeId = inParameter ? decodeUrlQueryValue(inParameter) : timeParamElement;
      const id = `event-${timeId}`;
      const idElement = document.getElementById(id);
      if (idElement && remarks?.includes(timeId)) {
        //Reset class
        const className = 'parliament-live-event-highlighted';
        const elements = document.getElementsByClassName(`${className}`);
        if (elements.length) {
          Array.from(elements).forEach((element) => element.classList.remove(className));
        }
        const item = document.getElementById(id);
        const highlightedKeyword = item?.querySelector('em.policymogul-keyword-match');
        item.classList.add(className);
        const hitItem = hits?.find((hit) => {
          const parameter = getInParameter({ itemUrl: hit?.contentUrl });
          return parameter === timeId;
        });
        if (hitItem && navigateRemarks && !transcriptSearch.isActive) {
          navigateRemarks({ item: hitItem, source });
        }
        setTimeout(() => {
          const valueToPosition = location?.pathname === '/dashboard' ? 80 : 140;
          //OP: Adjust scroll osition to top, to make the highlighted keyword visible
          const highlightedKeywordOffset =
            location?.pathname === '/dashboard'
              ? ((highlightedKeyword?.offsetTop > 200 ? highlightedKeyword?.offsetTop - 100 : 0) ?? 0)
              : 0;
          const position = item.offsetTop - valueToPosition - highlightedKeywordOffset;
          if (scrollBarRef && scrollBarRef.current) {
            updatingContributionRef.current = true;
            scrollBarRef.current.scrollTop(position);
          }
        }, 1);
        const finalSearchParams = new URLSearchParams(location?.search);
        finalSearchParams.set('in', timeId);
        navigate({ search: finalSearchParams.toString() }, { replace: true });
        setTimeout(() => {
          updatingContributionRef.current = false;
        }, 150);
      }
    }
  };

  useEffect(() => {
    const source = new AbortController();
    updateExtract.current(source);
    return () => {
      source.abort('Highlight remark cancelled by the user');
    };
  }, [location.search]);

  useEffect(() => {
    if (contentContainer?.current) {
      const { left, width, height, top } = contentContainer.current?.getBoundingClientRect();
      setTranscriptionButtonPosition({
        left: left + (width - goTranscriptionButton?.current?.getBoundingClientRect()?.width) / 2,
        top: height + top - 60,
      });
    }
  }, [contentContainer?.current, goTranscriptionButton?.current, windowWidth, windowHeight]);

  const scrollToBottom = () => {
    if (allowScrollBottom.current && !scrolling) {
      setTimeout(
        () =>
          scrollBarRef?.current?.view?.scroll({
            top: scrollBarRef?.current?.view?.scrollHeight,
            behavior: 'smooth',
          }),
        10
      );
    }
  };

  const axiosCancelToken = useRef(null);
  const eventHighlightedTime = useRef(null);
  const visibleRowsRef = useRef(new Set());
  const scrollRafRef = useRef(null);
  const scrollTargetRef = useRef(null);

  const processScroll = () => {
    scrollRafRef.current = null;
    const node = scrollTargetRef.current;
    if (!node) return;

    const { scrollTop, scrollHeight, clientHeight } = node;
    const scrolledToBottom = scrollTop === scrollHeight - clientHeight;
    allowScrollBottom.current = scrolledToBottom;
    setReachedTheEnd?.(endedEvent && scrolledToBottom);
    setShowGoToTranscriptionButton(conditionToShowGoToTranscriptionButton(node));

    const containerHeight = contentContainer.current?.getBoundingClientRect()?.height ?? 0;
    const nextTopFade = scrollTop > 20;
    const nextBottomFade = scrollTop + containerHeight < scrollHeight - 50;
    setScrollFades((prev) =>
      prev.topFade === nextTopFade && prev.bottomFade === nextBottomFade
        ? prev
        : { topFade: nextTopFade, bottomFade: nextBottomFade }
    );

    if ((hits?.length ?? 0) > 0 && !updatingContributionRef.current) {
      const containerTop = contentContainer.current?.getBoundingClientRect()?.top ?? 0;
      let matchEl = null;
      let matchTop = Infinity;
      visibleRowsRef.current.forEach((el) => {
        const { top } = el.getBoundingClientRect();
        if (top >= containerTop && top < containerTop + 100 && top < matchTop) {
          matchTop = top;
          matchEl = el;
        }
      });
      const inParameter = matchEl?.getAttribute('id')?.replace('event-', '');
      if (inParameter && remarkTimeIds.has(inParameter) && eventHighlightedTime.current !== inParameter) {
        const className = 'parliament-live-event-highlighted';
        const currentElement = document.getElementsByClassName(className)[0];
        if (currentElement) currentElement.classList.remove(className);
        matchEl.classList.add(className);
        const finalSearchParams = new URLSearchParams(location?.search);
        finalSearchParams.set('in', inParameter);
        navigate({ search: finalSearchParams?.toString() }, { replace: true });
        if (axiosCancelToken.current) axiosCancelToken.current.abort();
        axiosCancelToken.current = new AbortController();
        if (navigateRemarks && !transcriptSearch.isActive) {
          navigateRemarks({ item: remarkTimeIds.get(inParameter), source: axiosCancelToken.current });
        }
        eventHighlightedTime.current = inParameter;
      }
    }
  };

  const handleScroll = (e) => {
    scrollTargetRef.current = e;
    if (scrollRafRef.current == null) {
      scrollRafRef.current = requestAnimationFrame(processScroll);
    }
  };

  useEffect(() => {
    return () => {
      if (scrollRafRef.current != null) cancelAnimationFrame(scrollRafRef.current);
    };
  }, []);

  useEffect(() => {
    const root = scrollBarRef.current?.view;
    if (!root || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) visibleRowsRef.current.add(entry.target);
          else visibleRowsRef.current.delete(entry.target);
        });
      },
      { root }
    );
    root.querySelectorAll('.parliament-live-event').forEach((row) => observer.observe(row));
    return () => {
      observer.disconnect();
      visibleRowsRef.current.clear();
    };
  }, [filteredBodies.length]);

  const { showFixedTitle, titleStyles } = useTitleScrolledStyles({ contentContainer, titleSection });
  const topFadeCondition = scrollFades.topFade && realTimeEvent?.actualStartDateTime;
  const bottomFadeCondition = scrollFades.bottomFade && realTimeEvent?.actualStartDateTime;

  const eventBodiesRef = useRef(eventBodies);

  const handleFinish = () => {
    let newEventBodies = [...eventBodiesRef.current];
    const currentItemPosition = newEventBodies.findIndex((item) => item?.objectID === animatedItem?.objectID);
    if (currentItemPosition >= 0) {
      newEventBodies[currentItemPosition] = { ...newEventBodies[currentItemPosition], notShown: false };
      const notShowItems = newEventBodies?.filter((item) => item?.notShown);
      setAnimatedItem(notShowItems?.length > 0 ? notShowItems[0] : null);
    }
    setEventBodies(newEventBodies);
  };

  useEffect(() => {
    const notShownItems = eventBodies?.filter((item) => item?.notShown);
    eventBodiesRef.current = eventBodies;
    if (!animatedItem && notShownItems?.length > 0) {
      setAnimatedItem(notShownItems[0]);
    }
  }, [eventBodies]);

  const openClipForItems = useCallback(
    (items) => {
      if (!items?.length) return;
      videoPlayerRef.current?.pause();
      const first = items[0];
      const last = items[items.length - 1];
      const videoStart = videoSecondsForItem(first, streamStartTimestamp) ?? 0;
      const lastStart = videoSecondsForItem(last, streamStartTimestamp) ?? videoStart;
      const videoEnd = Math.max(videoStart, lastStart + (last?.duration ?? 0));
      const videoDuration = videoPlayerRef.current?.getDuration?.() ?? 0;
      const win = computeClipWindow({ itemVideoStart: videoStart, itemVideoEnd: videoEnd, videoDuration });
      setClipPrep({
        status: 'preparing',
        item: first,
        itemVideoStart: videoStart,
        itemVideoEnd: videoEnd,
        videoDuration,
        ...win,
        poster: null,
        thumbnails: [],
        prepDone: false,
      });
    },
    [streamStartTimestamp, videoPlayerRef]
  );
  const startClipSelectionFromRow = useCallback(
    (item) => {
      videoPlayerRef.current?.pause();
      setClipSelectItem(item);
      setClipSelectActive(true);
    },
    [videoPlayerRef, setClipSelectActive]
  );

  return (
    <>
      <CustomScrollbar
        ref={scrollBarRef}
        className={`simple-scrollbar indented-scrollbar-small ${topFadeCondition ? 'scrollbar-top-fade' : ''} ${
          bottomFadeCondition ? 'scrollbar-bottom-fade' : ''
        }`}
        maximalThumbYSize={100}
        onScroll={(e) => {
          handleScroll(e);
        }}
        onScrollStart={() => {
          setScrolling(true);
        }}
        onScrollStop={() => {
          setScrolling(false);
        }}
      >
        <div className='main-update-content-container'>
          <div
            className={`section-title pb-3 keyupdate-content-title content-title-with-summary content-border-title`}
            ref={titleSection}
          >
            <div className='d-flex align-items-lg-center justify-content-between'>
              <div className='pr-lg-5 pr-3 content-new-title flex-grow-1'>
                <h3 className='main-title news-title pr-lg-4 mb-0'>{eventItem?.title}</h3>
              </div>
              <ShareEventButtons eventBodies={eventBodies} eventItem={eventItem} />
            </div>

            <ParliamentHeadingElement item={eventItem} />
          </div>
          {/* FIXED TITLE */}
          <div
            className={`content-item-scrolled-title section-title bg-white ${
              showFixedTitle ? 'item-scrolled-title-visible' : ''
            }`}
            style={titleStyles}
            ref={fixedTitle}
          >
            <div className='d-flex justify-content-between scrolled-title-container'>
              <h3 className='main-title news-title pr-lg-4 '>
                <span className='font-weight-bold'>{eventItem?.title}</span>
              </h3>

              <button
                className='nowrap-item general-button action-button rounded-pill specific-x-padding-18 specific-y-padding-2  flex-centered justify-content-center'
                disabled={clipSelectActive}
                onClick={() => {
                  videoPlayerRef.current?.pause();
                  setClipSelectItem(null);
                  setClipSelectActive(true);
                }}
              >
                Create a clip
              </button>
              <ParliamentEventFixedTitleMoreEvents eventBodies={eventBodies} />
            </div>
          </div>
          {/* END FIXED TITLE */}
          <TranscriptSearchBar search={transcriptSearch} fixedTitleRef={fixedTitle} />
          <div className='py-4 position-relative'>
            <>
              {filteredBodies.map((item, index) => (
                <TranscriptRow
                  key={item?.objectID ?? `body-${index}`}
                  item={item}
                  timeParam={timeParams[index]}
                  relevantRemark={relevantRemarkUrls.has(item?.contentUrl)}
                  showTime={showTimes[index]}
                  streamStartTimestamp={streamStartTimestamp}
                  videoPlayerRef={videoPlayerRef}
                  onCreateClip={startClipSelectionFromRow}
                  isPreparingClip={clipPrep.status === 'preparing' && clipPrep.item?.objectID === item?.objectID}
                  playingItemId={playingItemId}
                  setPlayingItemId={setPlayingItemId}
                />
              ))}
              {!!animatedItem && (
                <a
                  href={animatedItem?.sourceUrl ?? animatedItem?.contentUrl}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='reset-link text-main-blue px-4 d-block parliament-live-event py-1 break-word'
                >
                  <p className='paragraph-p1 line-height-1-5 mb-5'>
                    <TextGenerate
                      key={animatedItem?.objectID}
                      onFinish={handleFinish}
                      onCharacterAdd={(i) => {
                        if (i % 10 === 0) scrollToBottom();
                      }}
                    >
                      {animatedItem?.text}
                    </TextGenerate>
                  </p>
                </a>
              )}
            </>
            {eventItem?.transcriberRunning && <div style={{ height: 200 }} />}
            <ClipLineSelector
              active={clipSelectActive}
              preparing={clipPrep.status === 'preparing'}
              bodies={filteredBodies}
              initialItem={clipSelectItem}
              onCancel={() => {
                setClipSelectActive(false);
                setClipSelectItem(null);
                setClipPrep((prev) => (prev.status === 'preparing' ? { ...prev, status: 'idle' } : prev));
              }}
              onConfirm={(items) => openClipForItems(items)}
            />
          </div>
          <button
            ref={goTranscriptionButton}
            className='rounded-pill bg-main-blue px-4 py-2 general-button position-fixed text-white z-max go-to-live-transcription nowrap-item'
            style={{
              visibility: showGoToTranscriptionButton ? `visible` : 'hidden',
              opacity: !!transcriptionButtonPosition && showGoToTranscriptionButton && !clipSelectActive ? 1 : 0,
              pointerEvents: clipSelectActive ? 'none' : undefined,
              top: `${transcriptionButtonPosition?.top}px`,
              left: `${transcriptionButtonPosition?.left}px`,
            }}
            onClick={() => {
              setShowGoToTranscriptionButton(false);
              scrollBarRef?.current?.view?.scroll({
                top: scrollBarRef?.current?.view?.scrollHeight,
                behavior: 'smooth',
              });
              allowScrollBottom.current = true;
            }}
          >
            <span className='dropdown-item-element dropdown-item-element-11'>
              Go to {eventItem?.eventHasEnded ? 'end of transcription' : 'live transcription'}
            </span>
          </button>
        </div>
      </CustomScrollbar>
      {clipPrep.status !== 'idle' && !clipPrep.prepDone && videoSrc && (
        <div
          aria-hidden
          style={{ position: 'fixed', left: -99999, top: 0, width: 320, height: 180, pointerEvents: 'none' }}
        >
          <ClipPreviewPlayer
            masterPlaylistUrl={videoSrc}
            windowStart={clipPrep.windowStart}
            windowEnd={clipPrep.windowEnd}
            clipStart={clipPrep.clipStart}
            clipEnd={clipPrep.clipEnd}
            logoUrl={policyMogulLogoUrl}
            onPoster={(frame) => setClipPrep((p) => ({ ...p, poster: frame }))}
            onReady={() => {
              setClipPrep((p) => (p.status === 'preparing' ? { ...p, status: 'open' } : p));
              setClipSelectActive(false);
            }}
            onFrame={(frame) => setClipPrep((p) => ({ ...p, thumbnails: [...p.thumbnails, frame] }))}
            onThumbnails={() => setClipPrep((p) => ({ ...p, prepDone: true }))}
          />
        </div>
      )}
      <CreateClipModal
        isOpen={clipPrep.status === 'open'}
        setIsOpen={(isOpen) => setClipPrep((prev) => ({ ...prev, status: isOpen ? 'open' : 'idle' }))}
        masterPlaylistUrl={videoSrc}
        logoUrl={policyMogulLogoUrl}
        item={clipPrep.item}
        itemVideoStart={clipPrep.itemVideoStart}
        itemVideoEnd={clipPrep.itemVideoEnd}
        videoDuration={clipPrep.videoDuration}
        poster={clipPrep.poster}
        initialThumbnails={clipPrep.thumbnails}
      />
    </>
  );
};

const ShareEventButtons = (props) => {
  const { eventBodies } = props;
  const url = eventBodies[0]?.sourceUrl ?? eventBodies[0]?.contentUrl ?? window.location.origin;
  return (
    <div className={`flex-centered parliament-live-share-buttons `}>
      <button
        className={`general-button position-relative share-parliamentary-content d-inline-block-centered centered-2`}
        onClick={(e) => {
          preventLink(e);
          copyTextToClipboard(url, 'Link copied');
        }}
        data-tooltip-id={'general-tooltip'}
        data-tooltip-content={'Copy link to event on parliamentlive.tv'}
      >
        <span className={`icon-share-video title-h5-m`} />
      </button>
    </div>
  );
};

const ParliamentEventFixedTitleMoreEvents = (props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dimentions, setDimentions] = useState({ top: 0, left: 0, opacity: 0 });
  const moreActionsElement = useRef(null);
  const elementContainer = useRef(null);
  const hideActions = () => {
    setIsOpen(false);
  };
  useOutsideElement(moreActionsElement, hideActions);

  const { eventBodies } = props;
  const url = eventBodies[0]?.sourceUrl ?? eventBodies[0]?.contentUrl ?? window.location.origin;

  useLayoutEffect(() => {
    if (moreActionsElement?.current && elementContainer?.current) {
      const updateDimentions = () => {
        const { top, height, left, width } = moreActionsElement.current?.getBoundingClientRect() ?? {};
        const { width: elementWidth, height: elementHeight } = elementContainer?.current?.getBoundingClientRect() ?? {};
        const heighValue = top + height + 10;
        setDimentions({
          top: `${heighValue + elementHeight > window.innerHeight ? top - elementHeight - 10 : heighValue}px`,
          left: `${left + width - elementWidth}px`,
          opacity: 1,
        });
      };
      updateDimentions();
      window.addEventListener('resize', updateDimentions);
      return () => {
        window.removeEventListener('resize', updateDimentions);
      };
    }
  }, [isOpen]);

  return (
    <div ref={moreActionsElement}>
      <button
        className='hovered-button circle-button general-button circle-button-36 text-center'
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className='icon-three-dots light-60-text' />
      </button>
      {isOpen && (
        <>
          <div
            ref={elementContainer}
            className='box-shadow py-2 z-extra-max'
            style={{
              position: 'fixed',
              top: dimentions?.top,
              left: dimentions?.left,
              opacity: dimentions?.opacity,
            }}
            onClick={(e) => {
              preventLink(e);
              copyTextToClipboard(url, 'Link copied');
            }}
          >
            <button
              className='general-button list-action-button px-3 py-1 w-100 text-left d-flex'
              style={{ maxWidth: '230px' }}
            >
              <span className={`icon-share-video title-h5-m mr-3 specific-top-margin-5`} />
              <span>Copy link to event on parliamentlive.tv</span>
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default ParliamentLiveEventContentVideo;
